import { ChangeDetectionStrategy, Component, computed, DestroyRef, effect, inject, input, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router, RouterLink } from '@angular/router';
import { NgbDropdownModule } from '@ng-bootstrap/ng-bootstrap';
import { TranslateDirective } from '@ngx-translate/core';
import { merge } from 'rxjs';

import { IdentityClientService, IdentityCommonService, IdentityConstants, IdentityUserService } from '@auth';
import { EventService } from '@core';
import { SasAvatarComponent } from '../sas-avatar/sas-avatar.component';

const APAS_MODULES = ['tpo', 'planning', 'buildingcontrol', 'preapps', 'enforcements'];
const MY_ACCOUNT_MODULES = [...APAS_MODULES, 'landcharges', 'publicprotection'];

@Component({
      selector: 'sas-sign-user',
      standalone: true,
      imports: [RouterLink, TranslateDirective, NgbDropdownModule, SasAvatarComponent],
      templateUrl: './sas-sign-user.component.html',
      styleUrl: './sas-sign-user.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class SasSignUserComponent {
    private readonly destroyRef = inject(DestroyRef);
    private readonly eventService = inject(EventService);
    private readonly sasIdentityConstant = inject(IdentityConstants).identityConstants;
    private readonly identityUserService = inject(IdentityUserService);
    private readonly identityClientService = inject(IdentityClientService);
    readonly identityCommonService = inject(IdentityCommonService);
    private readonly router = inject(Router);

    readonly isLogged = signal(this.identityUserService.isLogged());
    readonly clientUrl = computed(() => this.identityClientService.get().url);
    readonly signOutPath = this.identityCommonService.getSignOutPath();
    readonly username = computed(() => this.isLogged() ? this.identityUserService.getUser()?.username || '' : '');
    readonly userRole = computed(() => this.isLogged() ? this.identityUserService.getUser()?.role || '' : '');
    readonly avatarInitials = computed(() =>
        this.username().split(' ').map((w: string) => w[0]).join('').slice(0, 2).toUpperCase()
    );

    readonly mobileBreakpoint = input<number>(767);
    private mql: MediaQueryList = window.matchMedia('(max-width: 767px)');
    readonly isMobile = signal(this.mql.matches);
    private readonly mqlHandler = (e: MediaQueryListEvent) => this.isMobile.set(e.matches);

    constructor() {
        effect(() => {
            this.mql.removeEventListener('change', this.mqlHandler);
            this.mql = window.matchMedia(`(max-width: ${this.mobileBreakpoint()}px)`);
            this.isMobile.set(this.mql.matches);
            this.mql.addEventListener('change', this.mqlHandler);
        });
        this.destroyRef.onDestroy(() => this.mql.removeEventListener('change', this.mqlHandler));
        this.setupListeners();
    }

    private setupListeners() {
        merge(
            this.eventService.getEvent(this.sasIdentityConstant.eventLoginSuccess),
            this.eventService.getEvent(this.sasIdentityConstant.eventRegisterSuccess),
            this.eventService.getEvent(this.sasIdentityConstant.eventLogoutSuccess),
            this.eventService.getEvent(this.sasIdentityConstant.eventSessionTimeout)
        )
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(() => this.isLogged.set(this.identityUserService.isLogged()));
    }

    navigateToProfile() {
        const currentUrl = this.router.url;
        const isApasModule = APAS_MODULES.some((route) => currentUrl.includes(`/${route}`));
        const isLandchargesModule = currentUrl.includes('/landcharges');
        const isPublicProtectionModule = currentUrl.includes('/publicprotection');
        const isAdmin = this.userRole() === 'ADMINISTRATOR';

        const activeModule = MY_ACCOUNT_MODULES.find((route) => currentUrl.includes(`/${route}`));

        if ((isApasModule && !isAdmin) || isLandchargesModule || isPublicProtectionModule) {
            this.router.navigate([`${this.clientUrl()}/${activeModule}/my-account`]);
        } else if (isApasModule && isAdmin) {
            this.router.navigate([`${this.clientUrl()}/${activeModule}/officer-details`]);
        } else {
            this.router.navigate([`${this.clientUrl()}/login-details`]);
        }
    }
}
